<script>
	// chat-builder/ImagePicker.svelte
	// V3 블록 이미지 자산 선택 모달 (image/card/gallery 공용).
	//
	// 저장은 assetId(ref)로 — 선택 시 { assetId, url, name }를 emit. 블록은 assetId를 저장하고,
	// 서버(BlockOrchestrator)가 서빙 시점에 current 버전 URL로 resolve한다(교체 일괄전파 + 이력 동결).
	//
	// 기능: 가상 폴더 트리(folderPath 슬래시 구분) / 검색 / 드래그 업로드 / 카드별 교체(새 버전)·버전기록·롤백·이동·이름변경·삭제 /
	//       single(라디오) · multi(체크박스 → 여러 장 추가) 모드.
	import { createEventDispatcher } from "svelte";
	import * as api from "./imageAssetApi";

	export let open = false;
	export let chatId;
	export let mode = "single"; // 'single' | 'multi'
	export let currentAssetId = null; // single: 현재 선택 강조
	export let existingAssetIds = []; // multi: 이미 담긴 것 표시
	export let embedded = false; // true면 모달이 아니라 탭/컨테이너 안 인라인 관리 뷰(선택 없이 업로드·폴더·버전·이름변경·삭제만)
	// 통합 라이브러리: 열릴 때 기본으로 랜딩할 문맥 폴더(시작점). null=전체, ""=루트, "profilePage/..."·"chat/..."=특정 폴더.
	// 챗 빌더는 미지정(null=전체, 기존 동작). 봇 빌더는 자기 문맥 폴더를 넘겨 그 폴더부터 보여준다.
	export let contextFolder = null;

	const dispatch = createEventDispatcher();

	// (임베드 관리뷰) PC 타일 크기: 작게/크게 → auto-fill minmax의 최소 타일폭. 모바일은 CSS로 1칸 강제.
	const tilePxOf = { s: 160, l: 240 };
	let embedTile = "l";

	let assets = [];
	let loading = false;
	let busy = ""; // 진행중 라벨(업로드/처리)
	let error = "";
	let lightboxUrl = null; // 더블클릭 시 원본 크게 보기

	/**
	 * ⚠️⚠️ **CORS 모드 그림이 "캐시 때문에" 빈칸이 되는 걸 되살린다.**
	 *
	 * 여기 `<img>` 들은 `crossorigin="anonymous"` 를 달고 있다 — 빌더가 Origin 없는 응답을
	 * 캐시에 남기면 **같은 URL 을 XHR 로 받는 게임 텍스처·음원이 CORS 로 막히기** 때문이다
	 * (game-builder/SoundPicker.svelte 주석). 그 대가로 **반대 방향 사고**가 생긴다:
	 *
	 *   S3 는 Origin 없는 응답에 `Access-Control-Allow-Origin` 도 `Vary` 도 `Cache-Control` 도
	 *   붙이지 않는다(실측). 그래서 같은 URL 을 **no-cors 로 먼저** 받으면 — CSS
	 *   `background-image`(런처 프리뷰 배경이 그렇다), 새 탭으로 열기, 이메일 썸네일 등 —
	 *   **ACAO 없는 사본**이 캐시에 남고, 뒤이은 `crossorigin` 요청이 그 사본을 재사용해
	 *   CORS 검사에서 막힌다. 증상은 "새 탭에선 보이는데 라이브러리 타일만 빈칸".
	 *
	 * → 쿼리를 하나 붙여 **다른 캐시 키**로 한 번만 다시 받는다(S3 는 모르는 쿼리를 무시한다).
	 * ⚠️ origin 자체가 버킷 CORS 허용목록에 없으면(예: `http://127.0.0.1`, `localhost:포트`)
	 *    재시도도 실패하는 게 **맞다** — 거기서 가려 버리면 원인을 영원히 못 찾는다.
	 */
	const retryCorsImage = (e) => {
		const img = e.currentTarget;
		if (!img || img.dataset.corsRetried) return;
		img.dataset.corsRetried = "1";
		img.src = `${img.src}${img.src.includes("?") ? "&" : "?"}cors=1`;
	};
	let loadedForChat = null;

	let search = "";
	let selectedFolder = null; // null=전체, ""=루트, "a/b"=폴더
	// 점진적 공개: 문맥(봇/챗)이 있으면 — 모달이든 관리탭이든 — 기본 "접힘"(폴더 트리 숨기고 본인 문맥 것만 평평하게).
	// "전체 라이브러리"로 펼치면 폴더 트리+전 계정 탐색. 문맥 없는 메인 대시보드(/assets)만 기본 트리.
	let showLibrary = false;
	let transientFolders = []; // 아직 자산이 없는 새 폴더(가상)
	let multiSelected = new Set();

	// 오버레이 상태
	let versionAsset = null; // 버전 드로어 대상
	let versions = [];
	let moveTargets = null; // {assetIds:[...]} 이동 팝오버
	let dragOver = false;
	let dragAssetIds = []; // 내부 카드 드래그(폴더 이동) 중인 자산 id들
	let dragOverFolder = null; // 드롭 하이라이트 대상 폴더 경로
	let selectedIds = new Set(); // (임베드 관리뷰) 다중선택 — 일괄 이동용
	let anchorId = null; // Shift 범위 선택 기준점(마지막 단일 클릭)

	// 서버가 세션 오너로 자산을 스코프하므로 chatId 없이(봇 빌더)도 동작. scopeKey로 로드 가드.
	$: scopeKey = chatId ?? "__owner__";
	$: if ((open || embedded) && loadedForChat !== scopeKey) load();
	$: if (!open && !embedded) {
		// 닫힐 때 초기화(모달 전용)
		multiSelected = new Set();
		versionAsset = null;
		moveTargets = null;
		search = "";
		showLibrary = false; // 다음에 열 때 다시 "접힘"(문맥 폴더)부터
	}

	async function load() {
		loading = true;
		error = "";
		loadedForChat = scopeKey;
		selectedFolder = contextFolder; // 문맥 폴더로 랜딩(null=전체, 봇 빌더는 자기 폴더)
		try {
			assets = await api.listImageAssets(chatId);
		} catch (e) {
			error = e.message || "목록을 불러오지 못했습니다.";
		} finally {
			loading = false;
		}
	}

	// ── 폴더 트리 (자산 folderPath + 가상 폴더에서 유도) ──
	function ancestorsOf(path) {
		const out = [];
		let acc = "";
		for (const part of path.split("/")) {
			acc = acc ? acc + "/" + part : part;
			out.push(acc);
		}
		return out;
	}
	$: folderNodes = (() => {
		const set = new Set();
		for (const a of assets) {
			const p = a.folderPath || "";
			if (p) ancestorsOf(p).forEach((x) => set.add(x));
		}
		for (const t of transientFolders) if (t) ancestorsOf(t).forEach((x) => set.add(x));
		return [...set].sort((a, b) => a.localeCompare(b)).map((p) => ({
			path: p,
			name: p.split("/").pop(),
			depth: p.split("/").length - 1,
			count: assets.filter((a) => (a.folderPath || "") === p).length,
		}));
	})();
	$: rootCount = assets.filter((a) => !(a.folderPath || "")).length;
	// 접힘(문맥 있음)일 때 사이드바를 문맥 서브트리로 스코프: 문맥+그 하위 폴더만, 상위/다른 문맥은 숨김.
	// 들여쓰기(dep)는 문맥을 0으로 한 상대 깊이. 펼침/메인은 전체 folderNodes 그대로.
	$: contextDepth = contextFolder ? contextFolder.split("/").length : 0;
	$: sidebarFolders = (
		treeVisible
			? folderNodes
			: folderNodes.filter((f) => contextFolder && (f.path === contextFolder || f.path.startsWith(contextFolder + "/")))
	).map((f) => ({ ...f, dep: treeVisible ? f.depth : Math.max(0, f.depth - contextDepth + 1) }));

	// ── 표시 자산 (검색 우선, 없으면 폴더 필터) ──
	// 이름 검색은 NFC로 정규화해서 비교한다. macOS 업로드 파일명은 NFD(자모 분해)로 저장되는데
	// 검색 입력은 NFC(완성형)라, 정규화하지 않으면 한글이 `includes`로 안 잡힌다.
	$: treeVisible = showLibrary || !contextFolder; // 문맥(봇/챗) 있으면 기본 접힘(본인 것만), 문맥 없으면(메인 대시보드) 전체 트리
	$: visibleAssets = assets.filter((a) => {
		const p = a.folderPath || "";
		// 접힘(문맥 있음): 문맥 서브트리 밖은 제외(검색 포함)
		if (!treeVisible && contextFolder && !(p === contextFolder || p.startsWith(contextFolder + "/"))) return false;
		const q = search.trim().toLowerCase().normalize("NFC");
		if (q) return (a.name || "").toLowerCase().normalize("NFC").includes(q);
		if (selectedFolder === null) return true;                          // 전체(펼침/메인)
		if (!treeVisible && selectedFolder === contextFolder) return true; // 접힘 & 문맥 루트 선택 → 문맥 전체(서브트리 평평)
		return p === selectedFolder;                                       // 특정 폴더 직속
	});

	// 업로드 대상 폴더: 특정 폴더 선택 시 그 폴더, 전체/루트면 ""
	$: uploadFolder = selectedFolder === null ? "" : selectedFolder;

	// ── 선택 ──
	function pickSingle(a) {
		dispatch("select", { assetId: a.id, url: a.url, name: a.name });
		close();
	}
	function toggleMulti(a) {
		if (existingAssetIds.includes(a.id)) return;
		const s = new Set(multiSelected);
		s.has(a.id) ? s.delete(a.id) : s.add(a.id);
		multiSelected = s;
	}
	// 지금 보이는 것(고른 폴더·검색 결과) 중 담을 수 있는 걸 한꺼번에. 다 골라져 있으면 푼다.
	// 카드 수십 장을 한 번에 만들 때(런처 레시피) 하나씩 체크하지 않게 하려는 것.
	$: selectable = visibleAssets.filter((a) => !existingAssetIds.includes(a.id));
	$: allVisibleSelected = selectable.length > 0 && selectable.every((a) => multiSelected.has(a.id));
	function toggleSelectAll() {
		const s = new Set(multiSelected);
		if (allVisibleSelected) selectable.forEach((a) => s.delete(a.id));
		else selectable.forEach((a) => s.add(a.id));
		multiSelected = s;
	}
	function confirmMulti() {
		const items = assets
			.filter((a) => multiSelected.has(a.id))
			.map((a) => ({ assetId: a.id, url: a.url, name: a.name }));
		if (items.length) dispatch("add", { items });
		close();
	}
	function onCardClick(a, e) {
		if (embedded) {
			// 관리 뷰 다중선택(폴더 일괄 이동용)
			//  - 일반 클릭: 그 카드만 단일 선택(+범위 기준점)
			//  - Shift+클릭: 기준점 ~ 현재 카드까지 범위 선택
			//  - Ctrl/⌘+클릭: 개별 토글
			if (e && (e.metaKey || e.ctrlKey)) {
				toggleManageSelect(a.id);
				anchorId = a.id;
			} else if (e && e.shiftKey && anchorId != null) {
				selectRangeTo(a.id);
			} else {
				selectedIds = new Set([a.id]);
				anchorId = a.id;
			}
			return;
		}
		if (mode === "multi") toggleMulti(a);
		else pickSingle(a);
	}
	function toggleManageSelect(id) {
		const s = new Set(selectedIds);
		s.has(id) ? s.delete(id) : s.add(id);
		selectedIds = s;
	}
	function selectRangeTo(id) {
		const ids = visibleAssets.map((x) => x.id);
		const i1 = ids.indexOf(anchorId);
		const i2 = ids.indexOf(id);
		if (i1 === -1 || i2 === -1) {
			selectedIds = new Set([id]);
			anchorId = id;
			return;
		}
		const [lo, hi] = i1 <= i2 ? [i1, i2] : [i2, i1];
		selectedIds = new Set(ids.slice(lo, hi + 1)); // 앵커 유지(연속 범위 조정 가능)
	}
	function selectFolder(path) {
		selectedFolder = path;
		selectedIds = new Set(); // 폴더 전환 시 선택 초기화(숨은 선택 혼동 방지)
		anchorId = null;
	}

	// ── AI 생성 ──
	// 라이브러리 안에서 바로 만든다. 만든 결과는 **보고 있던 폴더**에 자산으로 들어간다.
	//
	// ⚠️ 챗 빌더의 AIImagePopup(마법사)과 다른 물건이다. 저기는 프로필/배경을 단계별로 좁혀가는
	//    저작 흐름이고, 여기는 "라이브러리에 한 장 더 만들어 넣기"다. 그래서 입력이 최소다.
	// ⚠️ 참조 이미지는 **지금 고른 자산**을 쓴다 — 라이브러리 안이라 따로 올릴 이유가 없다.
	//    참조가 있으면 image-to-image 로 그 그림의 종류·화풍을 이어받는다(상태 세트 만들 때 핵심).
	const AI_TYPES = [
		{ value: "sprite", label: "스프라이트", hint: "투명 배경 · 정사각 (게임 캐릭터·적·아이템)" },
		{ value: "background", label: "배경", hint: "불투명 · 세로 2:3" },
		{ value: "profile", label: "인물·캐릭터", hint: "불투명 · 정사각" },
	];
	let aiOpen = false;
	let aiPrompt = "";
	let aiType = "sprite";
	let aiUseRef = false;
	let aiError = "";

	// 참조로 쓸 자산: 임베드 뷰에서 고른 것 중 첫 번째, 모달이면 현재 선택된 것.
	$: aiRefAsset =
		assets.find((a) => (embedded ? selectedIds.has(a.id) : a.id === currentAssetId)) || null;
	$: if (!aiRefAsset) aiUseRef = false; // 참조가 사라지면 체크도 내려간다(없는 걸 켜둔 채로 두지 않게)

	async function runAiCreate() {
		const prompt = aiPrompt.trim();
		if (!prompt || busy) return;
		aiError = "";
		busy = "AI 이미지 만드는 중… (20초쯤 걸려요)";
		try {
			await api.aiCreateAsset(chatId, {
				prompt,
				imageType: aiType,
				// 생성물이 S3에 떨어질 자리. 자산 등록은 이 경로를 그대로 가리킨다(복사 없음).
				targetPath: aiType === "background" ? "images" : `${aiType}-images`,
				folderPath: uploadFolder,
				referenceImage: aiUseRef && aiRefAsset ? aiRefAsset.url : undefined,
			});
			aiPrompt = "";
			aiOpen = false;
			await load();
		} catch (e) {
			aiError = e.message || "이미지 생성에 실패했습니다.";
		} finally {
			busy = "";
		}
	}

	// ── 업로드 ──
	function openFilePicker() {
		const input = document.createElement("input");
		input.type = "file";
		input.accept = "image/*";
		input.multiple = true;
		input.onchange = (e) => uploadFiles(Array.from(e.target.files ?? []));
		input.click();
	}
	async function uploadFiles(files) {
		if (!files.length) return;
		busy = `업로드 중… (0/${files.length})`;
		let done = 0;
		try {
			for (const file of files) {
				await api.createAsset(chatId, file, uploadFolder);
				busy = `업로드 중… (${++done}/${files.length})`;
			}
			await load();
		} catch (e) {
			error = e.message || "업로드 실패";
		} finally {
			busy = "";
		}
	}
	function onDrop(e) {
		e.preventDefault();
		dragOver = false;
		const files = Array.from(e.dataTransfer?.files ?? []).filter((f) => f.type.startsWith("image/"));
		if (files.length) uploadFiles(files);
	}

	// ── 카드 액션 ──
	function replaceAsset(a) {
		const input = document.createElement("input");
		input.type = "file";
		input.accept = "image/*";
		input.onchange = async (e) => {
			const file = e.target.files?.[0];
			if (!file) return;
			busy = "교체 중…";
			try {
				await api.replaceAsset(chatId, a.id, file);
				await load();
			} catch (err) {
				error = err.message || "교체 실패";
			} finally {
				busy = "";
			}
		};
		input.click();
	}
	async function renameAsset(a) {
		const name = prompt("이미지 이름", a.name || "");
		if (name == null || name === a.name) return;
		try {
			await api.renameAsset(chatId, a.id, name);
			assets = assets.map((x) => (x.id === a.id ? { ...x, name } : x));
		} catch (e) {
			error = e.message || "이름변경 실패";
		}
	}
	async function removeAsset(a) {
		if (!confirm(`'${a.name || "이미지"}'를 삭제할까요?\n(이미 보낸 대화 이력의 이미지는 그대로 유지됩니다.)`)) return;
		try {
			await api.deleteAsset(chatId, a.id);
			assets = assets.filter((x) => x.id !== a.id);
		} catch (e) {
			error = e.message || "삭제 실패";
		}
	}

	// ── 버전 드로어 ──
	async function openVersions(a) {
		versionAsset = a;
		versions = [];
		try {
			versions = await api.listAssetVersions(a.id);
		} catch (e) {
			error = e.message || "버전 조회 실패";
		}
	}
	async function rollback(v) {
		if (!versionAsset) return;
		busy = "되돌리는 중…";
		try {
			await api.rollbackAsset(versionAsset.id, v.id);
			await load();
			versionAsset = assets.find((x) => x.id === versionAsset.id) || null;
			if (versionAsset) versions = await api.listAssetVersions(versionAsset.id);
		} catch (e) {
			error = e.message || "롤백 실패";
		} finally {
			busy = "";
		}
	}

	// ── 이동 팝오버 ──
	function openMove(assetIds) {
		moveTargets = { assetIds: [...assetIds] };
	}
	async function doMove(folderPath) {
		if (!moveTargets) return;
		busy = "이동 중…";
		try {
			for (const id of moveTargets.assetIds) await api.moveAsset(chatId, id, folderPath);
			assets = assets.map((a) =>
				moveTargets.assetIds.includes(a.id) ? { ...a, folderPath } : a,
			);
		} catch (e) {
			error = e.message || "이동 실패";
		} finally {
			busy = "";
			moveTargets = null;
		}
	}
	function newFolder() {
		// 현재 위치(선택 폴더, 없으면 문맥 폴더) 아래에 만든다 — 상위 경로는 자동으로 붙이고 "이름만" 입력받는다.
		const base = selectedFolder && selectedFolder !== "" ? selectedFolder : (contextFolder || "");
		const rel = prompt(base ? `"${base}" 아래 새 폴더 이름` : "새 폴더 이름 (예: 배경)", "");
		if (!rel) return;
		const seg = rel.replace(/^\/+|\/+$/g, "").trim();
		if (!seg) return;
		const clean = base ? `${base}/${seg}` : seg;
		if (!transientFolders.includes(clean)) transientFolders = [...transientFolders, clean];
		selectedFolder = clean;
	}

	// ── 카드 → 폴더 드래그 이동 (드롭 시 확인창만) ──
	// shift+클릭으로 여러 장 선택 후 그 중 하나를 끌면 선택 전체가 함께 이동한다.
	function onCardDragStart(e, a) {
		dragAssetIds = embedded && selectedIds.has(a.id) ? [...selectedIds] : [a.id];
		if (e.dataTransfer) {
			e.dataTransfer.effectAllowed = "move";
			e.dataTransfer.setData("text/plain", dragAssetIds.join(","));
		}
	}
	function onCardDragEnd() {
		dragAssetIds = [];
		dragOverFolder = null;
	}
	function onFolderDragOver(e, path) {
		if (!dragAssetIds.length) return; // 파일 업로드 등 무관한 드래그는 무시
		e.preventDefault();
		if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
		dragOverFolder = path;
	}
	function onFolderDragLeave(path) {
		if (dragOverFolder === path) dragOverFolder = null;
	}
	async function onFolderDrop(e, path) {
		if (!dragAssetIds.length) return;
		e.preventDefault();
		e.stopPropagation();
		const ids = dragAssetIds;
		dragAssetIds = [];
		dragOverFolder = null;
		// 실제로 폴더가 바뀌는 대상만
		const targets = assets.filter((x) => ids.includes(x.id) && (x.folderPath || "") !== path);
		if (!targets.length) return;
		const label = path === "" ? "(폴더 없음)" : path;
		const msg =
			targets.length === 1
				? `'${targets[0].name || "이미지"}'을(를) '${label}' 폴더로 이동할까요?`
				: `이미지 ${targets.length}개를 '${label}' 폴더로 이동할까요?`;
		if (!confirm(msg)) return;
		busy = "이동 중…";
		try {
			for (const t of targets) await api.moveAsset(chatId, t.id, path);
			const moved = new Set(targets.map((t) => t.id));
			assets = assets.map((x) => (moved.has(x.id) ? { ...x, folderPath: path } : x));
			selectedIds = new Set(); // 이동 후 선택 해제
			anchorId = null;
		} catch (err) {
			error = err.message || "이동 실패";
		} finally {
			busy = "";
		}
	}

	function close() {
		open = false;
		dispatch("close");
	}
	function onRootClick() {
		if (!embedded) close(); // 인라인 모드에선 배경 클릭 닫기 없음
	}
	function onKeydown(e) {
		if (e.key === "Escape") {
			if (lightboxUrl) lightboxUrl = null;
			else if (versionAsset) versionAsset = null;
			else if (moveTargets) moveTargets = null;
			else if (!embedded) close();
		}
	}
</script>

<svelte:window on:keydown={open || embedded ? onKeydown : undefined} />

{#if open || embedded}
	<div class="ip-root" class:embed={embedded} on:click|self={onRootClick}>
		<div class="ip-panel" class:embed={embedded}>
			<!-- 헤더 -->
			<div class="ip-head">
				<div class="ip-title">이미지 라이브러리</div>
				<input
					class="ip-search"
					type="text"
					placeholder="이름으로 검색…"
					bind:value={search}
				/>
				{#if contextFolder}
					<button
						style="border:1px solid #d0d5dd;background:#fff;border-radius:6px;padding:4px 10px;font-size:12px;font-weight:600;cursor:pointer;white-space:nowrap;color:#344054;"
						title={showLibrary ? "이 항목만 보기" : "전체 라이브러리에서 찾기"}
						on:click={() => (showLibrary = !showLibrary)}
					>{showLibrary ? "← 이 항목만" : "전체 라이브러리"}</button>
				{/if}
				{#if !embedded}
					<button class="ip-x" title="닫기" on:click={close}>✕</button>
				{/if}
			</div>

			<div class="ip-body">
				<!-- 폴더 사이드바 -->
				<aside class="ip-folders">
					{#if treeVisible}
					<button
						class="ip-folder"
						class:active={selectedFolder === null}
						on:click={() => selectFolder(null)}
					>
						<span class="fi">🗂️</span> 전체 <span class="cnt">{assets.length}</span>
					</button>
					<button
						class="ip-folder"
						class:active={selectedFolder === ""}
						class:drop-target={dragOverFolder === ""}
						on:click={() => selectFolder("")}
						on:dragover={(e) => onFolderDragOver(e, "")}
						on:dragleave={() => onFolderDragLeave("")}
						on:drop={(e) => onFolderDrop(e, "")}
					>
						<span class="fi">📁</span> (폴더 없음) <span class="cnt">{rootCount}</span>
					</button>
					{/if}
					{#each sidebarFolders as f}
						<button
							class="ip-folder"
							class:active={selectedFolder === f.path}
							class:drop-target={dragOverFolder === f.path}
							style="padding-left:{10 + f.dep * 14}px"
							on:click={() => selectFolder(f.path)}
							on:dragover={(e) => onFolderDragOver(e, f.path)}
							on:dragleave={() => onFolderDragLeave(f.path)}
							on:drop={(e) => onFolderDrop(e, f.path)}
							title={f.path}
						>
							<span class="fi">📁</span>
							<span class="fname">{f.name}</span>
							<span class="cnt">{f.count}</span>
						</button>
					{/each}
					<button class="ip-newfolder" on:click={newFolder}>＋ 새 폴더</button>
				</aside>

				<!-- 그리드 -->
				<section
					class="ip-grid-wrap"
					class:drag={dragOver}
					on:dragover|preventDefault={() => { if (!dragAssetIds.length) dragOver = true; }}
					on:dragleave={() => (dragOver = false)}
					on:drop={onDrop}
				>
					{#if embedded}
						<!-- PC 타일 크기 선택(모바일은 CSS로 숨김 + 1칸 강제) -->
						<div class="ip-tilebar">
							<span class="ip-tilebar-label">타일 크기</span>
							<select class="ip-tile-select" bind:value={embedTile} aria-label="타일 크기">
								<option value="s">작게</option>
								<option value="l">크게</option>
							</select>
						</div>
					{/if}
					{#if loading}
						<div class="ip-empty">불러오는 중…</div>
					{:else if error}
						<div class="ip-empty err">{error}</div>
					{:else if visibleAssets.length === 0}
						<div class="ip-empty">
							{search.trim()
								? "검색 결과가 없습니다."
								: "이미지가 없습니다. 아래에서 업로드하거나 여기로 끌어다 놓으세요."}
						</div>
					{:else}
						<div class="ip-grid" style={embedded ? `--ip-tile:${tilePxOf[embedTile]}px` : ""}>
							{#each visibleAssets as a (a.id)}
								{@const added = existingAssetIds.includes(a.id)}
								{@const sel = mode === "multi" ? multiSelected.has(a.id) : currentAssetId === a.id}
								<div
									class="ip-card"
									class:sel
									class:added
									class:msel={embedded && selectedIds.has(a.id)}
									draggable="true"
									on:click={(e) => onCardClick(a, e)}
									on:dblclick={() => (lightboxUrl = a.url)}
									on:dragstart={(e) => onCardDragStart(e, a)}
									on:dragend={onCardDragEnd}
								>
									<div class="ip-thumb">
										<img crossorigin="anonymous" src={a.url} alt={a.name || ""} loading="lazy" on:error={retryCorsImage} />
										{#if a.versionCount > 1}
											<span class="ver-badge" title="{a.versionCount}개 버전">v{a.versionNo}</span>
										{/if}
										{#if mode === "multi"}
											<span class="check" class:on={sel}>{sel ? "✓" : ""}</span>
										{:else if sel}
											<span class="check on">✓</span>
										{:else if embedded && selectedIds.has(a.id)}
											<span class="check on">✓</span>
										{/if}
										{#if added}<span class="added-badge">추가됨</span>{/if}
										<!-- hover 액션 -->
										<div class="ip-actions" on:click|stopPropagation>
											<button data-tip="교체(새 버전)" aria-label="교체(새 버전)" on:click={() => replaceAsset(a)}>♻</button>
											<button data-tip="버전 기록" aria-label="버전 기록" on:click={() => openVersions(a)}>🕘</button>
											<button data-tip="폴더 이동" aria-label="폴더 이동" on:click={() => openMove([a.id])}>📂</button>
											<button data-tip="이름 변경" aria-label="이름 변경" on:click={() => renameAsset(a)}>✎</button>
											<button data-tip="삭제" aria-label="삭제" class="del" on:click={() => removeAsset(a)}>🗑</button>
										</div>
									</div>
									<div class="ip-name" title={a.name}>{a.name || "(이름 없음)"}</div>
								</div>
							{/each}
						</div>
					{/if}

					{#if dragOver}<div class="ip-dropmask">여기에 놓아 업로드</div>{/if}
				</section>
			</div>

			<!-- AI 생성 패널 -->
			{#if aiOpen}
				<div class="ip-ai">
					<div class="ip-ai-row">
						<select class="ip-ai-type" bind:value={aiType} disabled={!!busy}>
							{#each AI_TYPES as t (t.value)}
								<option value={t.value}>{t.label}</option>
							{/each}
						</select>
						<span class="ip-ai-hint">{AI_TYPES.find((t) => t.value === aiType)?.hint}</span>
					</div>
					<textarea
						class="ip-ai-prompt"
						rows="2"
						placeholder="무엇을 만들까요? (예: 붉은색 날카로운 적 비행체, 아래를 향함)"
						bind:value={aiPrompt}
						disabled={!!busy}
					></textarea>
					<div class="ip-ai-row">
						{#if aiRefAsset}
							<label class="ip-ai-ref">
								<input type="checkbox" bind:checked={aiUseRef} disabled={!!busy} />
								<img crossorigin="anonymous" src={aiRefAsset.url} alt="" on:error={retryCorsImage} />
								<span>이 이미지를 참조로 (화풍·종류를 이어받음)</span>
							</label>
						{:else}
							<span class="ip-ai-hint">
								목록에서 이미지를 하나 고르면 그걸 참조로 삼아 같은 화풍으로 만들 수 있어요.
							</span>
						{/if}
						<span class="spacer" />
						<button class="ip-cancel" on:click={() => (aiOpen = false)} disabled={!!busy}>취소</button>
						<button class="ip-add" on:click={runAiCreate} disabled={!!busy || !aiPrompt.trim()}>만들기</button>
					</div>
					{#if aiError}<div class="ip-ai-err">{aiError}</div>{/if}
				</div>
			{/if}

			<!-- 푸터 -->
			<div class="ip-foot">
				<button class="ip-upload" on:click={openFilePicker} disabled={!!busy}>
					⬆ 업로드{uploadFolder ? ` → ${uploadFolder}` : ""}
				</button>
				<button class="ip-upload" on:click={() => (aiOpen = !aiOpen)} disabled={!!busy}>
					✨ AI로 만들기
				</button>
				{#if busy}<span class="ip-busy">{busy}</span>{/if}
				{#if embedded && !busy}
					<span class="ip-selhint">클릭 후 Shift+클릭으로 범위 선택 · 폴더로 드래그해 이동</span>
				{/if}
				<span class="spacer" />
				{#if embedded && selectedIds.size}
					<span class="ip-selinfo">{selectedIds.size}개 선택</span>
					<button type="button" class="ip-selclear" on:click={() => (selectedIds = new Set())}>선택 해제</button>
				{/if}
				{#if !embedded && mode === "multi"}
					<button type="button" class="ip-selclear" disabled={selectable.length === 0} on:click={toggleSelectAll}>
						{allVisibleSelected ? "선택 해제" : `보이는 것 모두 선택 (${selectable.length})`}
					</button>
					<button class="ip-add" disabled={multiSelected.size === 0} on:click={confirmMulti}>
						{multiSelected.size}개 추가
					</button>
				{/if}
				{#if !embedded}
					<button class="ip-cancel" on:click={close}>닫기</button>
				{/if}
			</div>

			<!-- 버전 드로어 -->
			{#if versionAsset}
				<div class="ip-drawer" on:click|self={() => (versionAsset = null)}>
					<div class="ip-drawer-panel">
						<div class="ip-head">
							<div class="ip-title">버전 기록 — {versionAsset.name || ""}</div>
							<button class="ip-x" on:click={() => (versionAsset = null)}>✕</button>
						</div>
						<div class="ver-list">
							{#each versions as v}
								<div class="ver-item" class:cur={v.id === versionAsset.currentVersionId}>
									<img crossorigin="anonymous" src={v.url} alt="" loading="lazy" on:error={retryCorsImage} />
									<div class="ver-meta">
										<div class="ver-no">v{v.versionNo} {#if v.id === versionAsset.currentVersionId}<span class="cur-tag">현재</span>{/if}</div>
										<div class="ver-date">{(v.createdAt || "").toString().replace("T", " ").slice(0, 16)}</div>
									</div>
									{#if v.id !== versionAsset.currentVersionId}
										<button class="ver-roll" on:click={() => rollback(v)} disabled={!!busy}>이 버전으로</button>
									{/if}
								</div>
							{/each}
							{#if versions.length === 0}<div class="ip-empty">버전 정보를 불러오는 중…</div>{/if}
						</div>
						<div class="ip-foot">
							<button class="ip-upload" on:click={() => replaceAsset(versionAsset)} disabled={!!busy}>♻ 새 버전 업로드</button>
							{#if busy}<span class="ip-busy">{busy}</span>{/if}
						</div>
					</div>
				</div>
			{/if}

			<!-- 이동 팝오버 -->
			{#if moveTargets}
				<div class="ip-drawer" on:click|self={() => (moveTargets = null)}>
					<div class="ip-move-panel">
						<div class="ip-head">
							<div class="ip-title">이동할 폴더 ({moveTargets.assetIds.length}개)</div>
							<button class="ip-x" on:click={() => (moveTargets = null)}>✕</button>
						</div>
						<div class="move-list">
							<button class="move-opt" on:click={() => doMove("")}>📁 (폴더 없음)</button>
							{#each folderNodes as f}
								<button class="move-opt" style="padding-left:{12 + f.depth * 14}px" on:click={() => doMove(f.path)}>📁 {f.name}</button>
							{/each}
							<button class="move-opt new" on:click={() => { const n = prompt('새 폴더 경로'); if (n) doMove(n.replace(/^\/+|\/+$/g, '').trim()); }}>＋ 새 폴더로…</button>
						</div>
					</div>
				</div>
			{/if}
		</div>
	</div>

	{#if lightboxUrl}
		<!-- 더블클릭한 이미지 크게 보기 (배경 클릭/Esc로 닫기) -->
		<div class="ip-lightbox" role="dialog" aria-modal="true" on:click={() => (lightboxUrl = null)}>
			<img crossorigin="anonymous" src={lightboxUrl} alt="" on:click|stopPropagation on:error={retryCorsImage} />
			<button class="ip-lightbox-x" aria-label="닫기" on:click={() => (lightboxUrl = null)}>✕</button>
		</div>
	{/if}
{/if}

<style>
	.ip-root:not(.embed) {
		position: fixed;
		inset: 0;
		background: hsl(0 0% 0% / 0.5);
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 1000;
		padding: 24px;
	}
	.ip-panel {
		position: relative;
		width: min(920px, 96vw);
		height: min(640px, 90vh);
		background: hsl(var(--sb-card));
		border: 1px solid hsl(var(--sb-border));
		border-radius: 14px;
		display: flex;
		flex-direction: column;
		overflow: hidden;
		box-shadow: 0 20px 60px hsl(0 0% 0% / 0.35);
	}

	/* 인라인(탭 임베드) 모드: 오버레이/그림자 없이 컨테이너를 꽉 채움 */
	.ip-root.embed {
		display: block;
		width: 100%;
	}
	.ip-panel.embed {
		width: 100%;
		height: 640px;
		max-height: 72vh;
		box-shadow: none;
		border-radius: 12px;
	}
	.ip-panel.embed .ip-folders {
		width: 150px;
	}
	.ip-panel.embed .ip-card {
		cursor: pointer; /* 관리 뷰: shift+클릭 다중선택 */
	}
	/* 임베드(탭) 그리드: PC는 타일 크기(--ip-tile)에 따라 열 수 자동, 모바일은 아래 미디어쿼리로 1칸 */
	.ip-panel.embed .ip-grid {
		grid-template-columns: repeat(auto-fill, minmax(var(--ip-tile, 220px), 1fr));
	}
	/* 타일 크기 선택 (PC 전용) */
	.ip-tilebar {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		gap: 8px;
		margin-bottom: 10px;
	}
	.ip-tilebar-label {
		font-size: 12px;
		color: hsl(var(--sb-muted-foreground));
	}
	.ip-tile-select {
		height: 32px;
		padding: 0 30px 0 10px;
		font-size: 13px;
		color: hsl(var(--sb-foreground));
		background-color: hsl(var(--sb-card));
		border: 1px solid hsl(var(--sb-border));
		border-radius: 8px;
		cursor: pointer;
		appearance: none;
		-webkit-appearance: none;
		background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%238b8b8b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
		background-repeat: no-repeat;
		background-position: right 9px center;
	}
	.ip-tile-select:focus {
		outline: none;
		border-color: hsl(var(--sb-primary));
		box-shadow: 0 0 0 2px hsl(var(--sb-primary) / 0.2);
	}
	/* 모바일: 그리드 1칸 + 타일 크기 숨김 (base 뒤에 와야 우선 적용됨) */
	@media (max-width: 767px) {
		.ip-panel.embed .ip-grid {
			grid-template-columns: 1fr;
		}
		.ip-tilebar {
			display: none;
		}
	}
	/* 더블클릭 원본 크게 보기 */
	.ip-lightbox {
		position: fixed;
		inset: 0;
		z-index: 1100;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 24px;
		background: hsl(0 0% 0% / 0.8);
		cursor: zoom-out;
	}
	.ip-lightbox img {
		max-width: 92vw;
		max-height: 92vh;
		object-fit: contain;
		border-radius: 8px;
		cursor: default;
	}
	.ip-lightbox-x {
		position: absolute;
		top: 16px;
		right: 16px;
		width: 36px;
		height: 36px;
		border: none;
		border-radius: 999px;
		background: hsl(0 0% 100% / 0.15);
		color: #fff;
		font-size: 16px;
		cursor: pointer;
	}
	.ip-lightbox-x:hover {
		background: hsl(0 0% 100% / 0.28);
	}
	.ip-head {
		display: flex;
		align-items: center;
		gap: 10px;
		padding: 14px 16px;
		border-bottom: 1px solid hsl(var(--sb-border));
	}
	.ip-title {
		font-size: 15px;
		font-weight: 700;
		color: hsl(var(--sb-foreground));
		white-space: nowrap;
	}
	.ip-search {
		flex: 1;
		max-width: 320px;
		margin-left: auto;
		padding: 7px 12px;
		border: 1px solid hsl(var(--sb-border));
		border-radius: 8px;
		background: hsl(var(--sb-background, var(--sb-card)));
		color: hsl(var(--sb-foreground));
		font-size: 13px;
	}
	.ip-search:focus {
		outline: none;
		border-color: hsl(var(--sb-primary));
	}
	.ip-x {
		flex: none;
		width: 30px;
		height: 30px;
		border: none;
		border-radius: 8px;
		background: hsl(var(--sb-muted));
		color: hsl(var(--sb-foreground));
		cursor: pointer;
	}
	.ip-body {
		flex: 1;
		display: flex;
		min-height: 0;
	}
	.ip-folders {
		width: 200px;
		flex: none;
		border-right: 1px solid hsl(var(--sb-border));
		padding: 8px;
		overflow-y: auto;
		display: flex;
		flex-direction: column;
		gap: 2px;
	}
	.ip-folder {
		display: flex;
		align-items: center;
		gap: 6px;
		padding: 7px 10px;
		border: none;
		border-radius: 8px;
		background: transparent;
		color: hsl(var(--sb-foreground));
		font-size: 13px;
		cursor: pointer;
		text-align: left;
		width: 100%;
	}
	.ip-folder:hover {
		background: hsl(var(--sb-muted) / 0.6);
	}
	.ip-folder.active {
		background: hsl(var(--sb-primary) / 0.14);
		color: hsl(var(--sb-primary));
		font-weight: 600;
	}
	/* 카드를 끌어와 올렸을 때(드롭 대상) 강조 */
	.ip-folder.drop-target {
		background: hsl(var(--sb-primary) / 0.2);
		outline: 2px dashed hsl(var(--sb-primary));
		outline-offset: -2px;
		color: hsl(var(--sb-primary));
	}
	.ip-folder .fi {
		flex: none;
	}
	.ip-folder .fname {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	.ip-folder .cnt {
		margin-left: auto;
		font-size: 11px;
		color: hsl(var(--sb-muted-foreground));
	}
	.ip-newfolder {
		margin-top: 6px;
		padding: 7px 10px;
		border: 1px dashed hsl(var(--sb-border));
		border-radius: 8px;
		background: transparent;
		color: hsl(var(--sb-muted-foreground));
		font-size: 12px;
		cursor: pointer;
	}
	.ip-newfolder:hover {
		border-color: hsl(var(--sb-primary));
		color: hsl(var(--sb-primary));
	}
	.ip-grid-wrap {
		flex: 1;
		position: relative;
		overflow-y: auto;
		padding: 14px;
	}
	.ip-grid-wrap.drag {
		outline: 2px dashed hsl(var(--sb-primary));
		outline-offset: -8px;
	}
	.ip-grid {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
		gap: 12px;
	}
	.ip-card {
		border: 1px solid hsl(var(--sb-border));
		border-radius: 10px;
		overflow: hidden;
		background: hsl(var(--sb-background, var(--sb-card)));
		cursor: pointer;
		user-select: none; /* shift+클릭 다중선택 시 텍스트 드래그 방지 */
		transition: border-color 0.12s, transform 0.12s;
	}
	.ip-card:hover {
		border-color: hsl(var(--sb-primary) / 0.6);
	}
	.ip-card.sel,
	.ip-card.msel {
		border-color: hsl(var(--sb-primary));
		box-shadow: 0 0 0 2px hsl(var(--sb-primary) / 0.35);
	}
	.ip-card.added {
		opacity: 0.7;
	}
	.ip-thumb {
		position: relative;
		aspect-ratio: 1 / 1;
		background: hsl(var(--sb-muted));
	}
	.ip-thumb img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
	}
	.ver-badge {
		position: absolute;
		left: 6px;
		top: 6px;
		font-size: 10px;
		font-weight: 700;
		padding: 2px 6px;
		border-radius: 999px;
		background: hsl(0 0% 0% / 0.6);
		color: #fff;
	}
	.check {
		position: absolute;
		right: 6px;
		top: 6px;
		width: 20px;
		height: 20px;
		border-radius: 50%;
		border: 2px solid #fff;
		background: hsl(0 0% 0% / 0.35);
		color: #fff;
		font-size: 12px;
		display: flex;
		align-items: center;
		justify-content: center;
	}
	.check.on {
		background: hsl(var(--sb-primary));
		border-color: hsl(var(--sb-primary));
	}
	.added-badge {
		position: absolute;
		left: 50%;
		bottom: 8px;
		transform: translateX(-50%);
		font-size: 10px;
		padding: 2px 8px;
		border-radius: 999px;
		background: hsl(0 0% 0% / 0.6);
		color: #fff;
	}
	.ip-actions {
		position: absolute;
		left: 0;
		right: 0;
		bottom: 0;
		display: flex;
		gap: 2px;
		padding: 4px;
		background: linear-gradient(to top, hsl(0 0% 0% / 0.7), transparent);
		opacity: 0;
		transition: opacity 0.12s;
	}
	.ip-card:hover .ip-actions {
		opacity: 1;
	}
	.ip-actions button {
		position: relative;
		flex: 1;
		padding: 7px 0;
		border: none;
		border-radius: 5px;
		background: hsl(0 0% 100% / 0.9);
		color: #222;
		font-size: 15px;
		line-height: 1;
		cursor: pointer;
	}
	.ip-actions button:hover {
		background: #fff;
	}
	.ip-actions button.del:hover {
		background: hsl(var(--sb-destructive));
		color: #fff;
	}
	/* 아이콘 위에 즉시 뜨는 기능 설명 툴팁 */
	.ip-actions button::after {
		content: attr(data-tip);
		position: absolute;
		bottom: calc(100% + 6px);
		left: 50%;
		transform: translateX(-50%);
		padding: 3px 7px;
		border-radius: 6px;
		background: hsl(0 0% 0% / 0.9);
		color: #fff;
		font-size: 11px;
		font-weight: 500;
		line-height: 1.2;
		white-space: nowrap;
		pointer-events: none;
		opacity: 0;
		transition: opacity 0.12s;
		z-index: 6;
	}
	.ip-actions button:hover::after {
		opacity: 1;
	}
	/* 가장자리 버튼 툴팁은 카드 밖으로 잘리지 않게 정렬 */
	.ip-actions button:first-child::after {
		left: 0;
		transform: none;
	}
	.ip-actions button:last-child::after {
		left: auto;
		right: 0;
		transform: none;
	}
	.ip-name {
		padding: 6px 8px;
		font-size: 12px;
		color: hsl(var(--sb-foreground));
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	.ip-empty {
		display: flex;
		align-items: center;
		justify-content: center;
		height: 100%;
		min-height: 200px;
		color: hsl(var(--sb-muted-foreground));
		font-size: 13px;
		text-align: center;
		padding: 20px;
	}
	.ip-empty.err {
		color: hsl(var(--sb-destructive));
	}
	.ip-dropmask {
		position: absolute;
		inset: 8px;
		border-radius: 10px;
		background: hsl(var(--sb-primary) / 0.08);
		display: flex;
		align-items: center;
		justify-content: center;
		color: hsl(var(--sb-primary));
		font-weight: 700;
		pointer-events: none;
	}
	/* AI 생성 패널 — 푸터 바로 위에 붙어 "여기서 만들면 이 폴더에 들어간다"가 읽히게. */
	.ip-ai {
		display: flex;
		flex-direction: column;
		gap: 8px;
		padding: 12px 16px;
		border-top: 1px solid hsl(var(--sb-border));
		background: hsl(var(--sb-muted) / 0.4);
	}
	.ip-ai-row {
		display: flex;
		align-items: center;
		gap: 10px;
	}
	.ip-ai-row .spacer {
		flex: 1;
	}
	.ip-ai-type {
		height: 32px;
		padding: 0 8px;
		border: 1px solid hsl(var(--sb-border));
		border-radius: 8px;
		background: hsl(var(--sb-background));
		color: hsl(var(--sb-foreground));
		font-size: 13px;
		font-family: inherit;
	}
	.ip-ai-hint {
		font-size: 12px;
		color: hsl(var(--sb-muted-foreground));
	}
	.ip-ai-prompt {
		width: 100%;
		box-sizing: border-box;
		padding: 8px 10px;
		border: 1px solid hsl(var(--sb-border));
		border-radius: 8px;
		background: hsl(var(--sb-background));
		color: hsl(var(--sb-foreground));
		font-size: 13px;
		font-family: inherit;
		resize: vertical;
	}
	.ip-ai-ref {
		display: flex;
		align-items: center;
		gap: 8px;
		font-size: 12px;
		color: hsl(var(--sb-foreground));
		cursor: pointer;
	}
	.ip-ai-ref img {
		width: 32px;
		height: 32px;
		object-fit: contain;
		border-radius: 6px;
		/* 투명 PNG 가 많아 밝은 바탕에선 안 보인다 — 게임 화면과 비슷한 어두운 바탕에 둔다. */
		background: #0d1430;
	}
	.ip-ai-err {
		font-size: 12px;
		color: #d93025;
	}

	.ip-foot {
		display: flex;
		align-items: center;
		gap: 10px;
		padding: 12px 16px;
		border-top: 1px solid hsl(var(--sb-border));
	}
	.ip-foot .spacer {
		flex: 1;
	}
	.ip-upload {
		padding: 8px 14px;
		border: 1px solid hsl(var(--sb-primary));
		border-radius: 8px;
		background: hsl(var(--sb-primary) / 0.1);
		color: hsl(var(--sb-primary));
		font-size: 13px;
		font-weight: 600;
		cursor: pointer;
	}
	.ip-upload:disabled {
		opacity: 0.5;
		cursor: default;
	}
	.ip-busy {
		font-size: 12px;
		color: hsl(var(--sb-muted-foreground));
	}
	.ip-selhint {
		font-size: 12px;
		color: hsl(var(--sb-muted-foreground));
	}
	.ip-selinfo {
		font-size: 12px;
		font-weight: 600;
		color: hsl(var(--sb-primary));
	}
	.ip-selclear {
		margin-left: 8px;
		padding: 7px 12px;
		border: 1px solid hsl(var(--sb-border));
		border-radius: 8px;
		background: hsl(var(--sb-card));
		color: hsl(var(--sb-foreground));
		font-size: 12px;
		cursor: pointer;
	}
	.ip-selclear:hover {
		background: hsl(var(--sb-muted) / 0.6);
	}
	.ip-selclear:disabled {
		opacity: 0.4;
		cursor: default;
	}
	.ip-add {
		padding: 8px 16px;
		border: none;
		border-radius: 8px;
		background: hsl(var(--sb-primary));
		color: hsl(var(--sb-primary-foreground));
		font-size: 13px;
		font-weight: 600;
		cursor: pointer;
	}
	.ip-add:disabled {
		opacity: 0.4;
		cursor: default;
	}
	.ip-cancel {
		padding: 8px 14px;
		border: 1px solid hsl(var(--sb-border));
		border-radius: 8px;
		background: hsl(var(--sb-card));
		color: hsl(var(--sb-foreground));
		font-size: 13px;
		cursor: pointer;
	}

	/* 드로어 / 팝오버 */
	.ip-drawer {
		position: absolute;
		inset: 0;
		background: hsl(0 0% 0% / 0.35);
		display: flex;
		align-items: stretch;
		justify-content: flex-end;
		z-index: 5;
	}
	.ip-drawer-panel {
		width: min(420px, 90%);
		background: hsl(var(--sb-card));
		border-left: 1px solid hsl(var(--sb-border));
		display: flex;
		flex-direction: column;
	}
	.ver-list {
		flex: 1;
		overflow-y: auto;
		padding: 12px;
		display: flex;
		flex-direction: column;
		gap: 8px;
	}
	.ver-item {
		display: flex;
		align-items: center;
		gap: 10px;
		padding: 8px;
		border: 1px solid hsl(var(--sb-border));
		border-radius: 8px;
	}
	.ver-item.cur {
		border-color: hsl(var(--sb-primary));
		background: hsl(var(--sb-primary) / 0.06);
	}
	.ver-item img {
		width: 52px;
		height: 52px;
		flex: none;
		object-fit: cover;
		border-radius: 6px;
		border: 1px solid hsl(var(--sb-border));
	}
	.ver-meta {
		flex: 1;
		min-width: 0;
	}
	.ver-no {
		font-size: 13px;
		font-weight: 600;
		color: hsl(var(--sb-foreground));
	}
	.cur-tag {
		font-size: 10px;
		padding: 1px 6px;
		border-radius: 999px;
		background: hsl(var(--sb-primary));
		color: hsl(var(--sb-primary-foreground));
		margin-left: 4px;
	}
	.ver-date {
		font-size: 11px;
		color: hsl(var(--sb-muted-foreground));
	}
	.ver-roll {
		flex: none;
		padding: 6px 10px;
		border: 1px solid hsl(var(--sb-border));
		border-radius: 7px;
		background: hsl(var(--sb-card));
		color: hsl(var(--sb-primary));
		font-size: 12px;
		cursor: pointer;
	}
	.ip-move-panel {
		width: min(360px, 90%);
		margin: auto;
		background: hsl(var(--sb-card));
		border: 1px solid hsl(var(--sb-border));
		border-radius: 12px;
		overflow: hidden;
		align-self: center;
	}
	.move-list {
		padding: 8px;
		max-height: 360px;
		overflow-y: auto;
		display: flex;
		flex-direction: column;
		gap: 2px;
	}
	.move-opt {
		text-align: left;
		padding: 9px 12px;
		border: none;
		border-radius: 8px;
		background: transparent;
		color: hsl(var(--sb-foreground));
		font-size: 13px;
		cursor: pointer;
	}
	.move-opt:hover {
		background: hsl(var(--sb-muted) / 0.6);
	}
	.move-opt.new {
		color: hsl(var(--sb-primary));
		border-top: 1px solid hsl(var(--sb-border));
		margin-top: 4px;
	}
</style>
